import { DayPlan } from "@/clients/models/DayPlan";
import { Meal } from "@/clients/models/Meal";
import { MealTime } from "@/clients/models/MealTime";
import { Plan } from "@/clients/models/Plan";
import { WeekPlan } from "@/clients/models/WeekPlan";
import { isAfter, isValid, parseISO, startOfDay } from "date-fns";

export type PlanDayEntry = {
  day: string;
  date: Date;
  dateKey: string;
  meals: DayPlan["meals"];
  dayPlan: DayPlan;
  weekPlan: WeekPlan;
  week: string;
  weekIndex: number;
};

export type PlanSelectorDay = {
  day: string;
  date: Date;
  dateKey: string;
  isAllTickedOff: boolean;
};

const normalizeDay = (day: string): string => day.toLowerCase();

const buildEntry = ({
  dayPlan,
  dateKey,
  weekPlan,
  weekIndex,
  fallbackWeek,
}: {
  dayPlan: DayPlan;
  dateKey?: string | null;
  weekPlan: WeekPlan;
  weekIndex: number;
  fallbackWeek: string;
}): PlanDayEntry | null => {
  if (!dateKey) {
    return null;
  }

  const date = parseISO(dateKey);
  if (!isValid(date)) {
    return null;
  }

  return {
    day: normalizeDay(dayPlan.day),
    date,
    dateKey,
    meals: dayPlan.meals,
    dayPlan,
    weekPlan,
    week: weekPlan.week || fallbackWeek,
    weekIndex,
  };
};

const sortEntriesByDate = (entries: PlanDayEntry[]) =>
  [...entries].sort((a, b) => a.date.getTime() - b.date.getTime());

export const getPlanDayEntries = (plan?: Plan): PlanDayEntry[] => {
  if (!plan?.plan?.length) {
    return [];
  }

  const weeks: WeekPlan[] = plan.previousWeek ? [plan.previousWeek, ...plan.plan] : plan.plan;

  const nextFormatEntries: PlanDayEntry[] = [];
  weeks.forEach((weekPlan, weekIndex) => {
    weekPlan.data.forEach((dayPlan) => {
      const entry = buildEntry({
        dayPlan,
        dateKey: dayPlan.dayDate,
        weekPlan,
        weekIndex,
        fallbackWeek: (weekIndex + 1).toString(),
      });

      if (entry) {
        nextFormatEntries.push(entry);
      }
    });
  });

  if (nextFormatEntries.length > 0) {
    return sortEntriesByDate(nextFormatEntries);
  }

  // Legacy fallback: old payload stored both weeks in plan[0].data.
  const legacyWeekPlan = plan.plan[0];
  if (!legacyWeekPlan) {
    return [];
  }

  const legacyEntries: PlanDayEntry[] = [];
  legacyWeekPlan.data.forEach((dayPlan) => {
    const currentWeekEntry = buildEntry({
      dayPlan,
      dateKey: dayPlan.dayDateCurrentWeek,
      weekPlan: legacyWeekPlan,
      weekIndex: 0,
      fallbackWeek: "1",
    });
    if (currentWeekEntry) {
      legacyEntries.push(currentWeekEntry);
    }

    const nextWeekEntry = buildEntry({
      dayPlan,
      dateKey: dayPlan.dayDateNextWeek,
      weekPlan: legacyWeekPlan,
      weekIndex: 1,
      fallbackWeek: "2",
    });
    if (nextWeekEntry) {
      legacyEntries.push(nextWeekEntry);
    }
  });

  return sortEntriesByDate(legacyEntries);
};

export const getPlanDayEntryByDateKey = (
  plan: Plan | undefined,
  dateKey?: string | null,
): PlanDayEntry | undefined => {
  if (!dateKey) {
    return undefined;
  }

  return getPlanDayEntries(plan).find((entry) => entry.dateKey === dateKey);
};

export const getPlanDayEntry = (
  plan: Plan | undefined,
  day: string,
  dateKey?: string | null,
): PlanDayEntry | undefined => {
  const entries = getPlanDayEntries(plan);
  if (entries.length === 0) {
    return undefined;
  }

  const entryByDateKey = getPlanDayEntryByDateKey(plan, dateKey);
  if (entryByDateKey) {
    return entryByDateKey;
  }

  const normalizedDay = normalizeDay(day);
  const dayEntries = entries.filter((entry) => entry.day === normalizedDay);
  if (dayEntries.length === 0) {
    return entries[0];
  }

  const todayStart = startOfDay(new Date());
  const todayOrNextEntry = dayEntries.find((entry) => !isAfter(todayStart, startOfDay(entry.date)));
  return todayOrNextEntry ?? dayEntries[0];
};

export type DayMealItem = {
  meal: Meal;
  mealTime: MealTime;
  /** Index of the meal within its meal-time group (not the flat list). Kept so
   *  MealCard's detail navigation and change-tracking key stay stable. */
  mealIndex: number;
};

/** Fixed order the day's meals are laid out in the label-less "Your Day" list. */
const MEAL_TIME_ORDER: MealTime[] = [
  MealTime.BREAKFAST,
  MealTime.LUNCH,
  MealTime.DINNER,
  MealTime.SNACK,
];

/**
 * Flattens a plan day into a single ordered list of meals (breakfast → lunch →
 * dinner → snack), dropping the meal-time grouping for the My Plan v2 "Your Day"
 * list while preserving each meal's per-meal-time index.
 */
export const getDayMealItems = (
  plan: Plan | undefined,
  day: string,
  dateKey?: string | null,
): DayMealItem[] => {
  const meals = getPlanDayEntry(plan, day, dateKey)?.meals ?? [];
  const items: DayMealItem[] = [];

  MEAL_TIME_ORDER.forEach((mealTime) => {
    const group = meals
      .filter((mealPlan) => mealPlan.mealTime === mealTime)
      .flatMap((mealPlan) => mealPlan.data);
    group.forEach((meal, mealIndex) => {
      items.push({ meal, mealTime, mealIndex });
    });
  });

  return items;
};

export const getPlanSelectorDays = (plan?: Plan): PlanSelectorDay[] => {
  const todayStart = startOfDay(new Date());

  return getPlanDayEntries(plan).map((entry) => {
    const allMeals = entry.meals.flatMap((mealTime) => mealTime.data);
    const isFutureDay = isAfter(startOfDay(entry.date), todayStart);
    const isAllTickedOff =
      !isFutureDay && allMeals.length > 0 && allMeals.every((meal) => meal.isTickedOff === true);

    return {
      day: entry.day,
      date: entry.date,
      dateKey: entry.dateKey,
      isAllTickedOff,
    };
  });
};

/**
 * Moves meals the coach just added (ids in `recentIds`, newest first) to the
 * front of the day's list, in that order; every other meal keeps its place.
 * `mealIndex` is untouched so backend calls keyed on a meal's position inside
 * its meal-time group stay correct.
 */
export const hoistRecentlyAdded = (items: DayMealItem[], recentIds: string[]): DayMealItem[] => {
  if (recentIds.length === 0 || items.length === 0) return items;
  const rank = new Map(recentIds.map((id, index) => [id, index]));
  const rankOf = (item: DayMealItem): number | undefined =>
    item.meal.id ? rank.get(item.meal.id) : undefined;
  const hoisted = items
    .filter((item) => rankOf(item) !== undefined)
    .sort((a, b) => (rankOf(a) ?? 0) - (rankOf(b) ?? 0));
  if (hoisted.length === 0) return items;
  const rest = items.filter((item) => rankOf(item) === undefined);
  return [...hoisted, ...rest];
};
